// The API's `{ error: { code, message } }` envelope, as an exception - and what
// a screen asks of one: whether the session is gone, whether the request itself
// was refused, and what sentence to put in front of a person.
//
// Its own module rather than part of `client.ts` because `fixtures.ts` refuses
// things too: it stands in for the SERVER, so it has to be able to answer 409
// at the schedule cap and 404 for an id that is not there. Both of those live
// in `client.ts` in the sense that matters - it re-exports them, and no
// component imports this file - but a shared type that both a module and its
// own stand-in need is not one either of them should own.

/** The `{ error: { code, message } }` envelope, as an exception. */
export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  readonly outcomeUnknown: boolean;
  readonly ambiguous: boolean;

  constructor(status: number, code: string, message: string, details?: unknown, ambiguous = false) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.outcomeUnknown = ambiguous || status === 0;
    this.ambiguous = ambiguous;
  }
}

/**
 * Whether a failure means "nobody is signed in" - the one failure that is not a
 * failure of the request but of the session behind it, and the only one no
 * amount of trying again can fix.
 *
 * On the STATUS rather than on `code`, which is the exception to this client's
 * own rule that it switches on the code. The rule protects a client from prose;
 * `UNAUTHENTICATED` is the code and the server does send it. But the envelope is
 * the application's, and the request travels through a proxy that is not the
 * application: an authenticating layer in front of it answers 401 with its own
 * body, and `code` then falls back to `INTERNAL`. Reading that as a retryable
 * failure is exactly the dead "Try again" this replaced, so the question is
 * asked of the status line, which no intermediary can get wrong.
 */
export function isSignedOut(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 401;
}

/**
 * The sentence to put in front of a person when a request failed.
 *
 * Here rather than in each screen because a request is made from several places
 * - the day, the sheets, the Notes screen - and a copy of this in each would be
 * as many vocabularies for one server. The server's own prose is preferred
 * where there is any: `message` is written for a human, which is exactly why
 * clients switch on `code` and never on it.
 */
export function messageFor(error: unknown): string {
  if (error instanceof ApiClientError && error.code === 'NETWORK') {
    return 'Could not reach Perfect Note.';
  }
  return error instanceof ApiClientError ? error.message : 'Something went wrong.';
}

/**
 * The most specific sentence there is about a failure.
 *
 * The API's `parse()` answers a refused request with `details` - one entry per
 * field it would not take, each carrying a sentence written for a person and
 * with the LIMIT in it. `message` beside them is the envelope's own line ("That
 * is not a valid request body"), which is true and useless: somebody whose note
 * title is too long cannot act on it, and a screen that draws it leaves them
 * pressing a button that can only be refused again.
 *
 * So the details are preferred where there are any, and `messageFor` is the
 * answer where there are none. The reading of the shape is here rather than in
 * a screen because a component reaching into an error's `details` would be a
 * second place that knows what the API's envelope looks like - and because two
 * screens phrasing the same failure two ways is the thing `messageFor` exists
 * to prevent.
 */
export function reasonFor(error: unknown): string {
  const said = fieldMessages(error);
  return said.length === 0 ? messageFor(error) : said.join(' ');
}

/**
 * Whether the server refused the REQUEST rather than failed to serve it.
 *
 * The one question a screen has to ask before offering to try again: sending
 * the same thing to a 400 can only be refused again, and a dead end dressed as
 * a retry is worse than an honest refusal, because the person keeps pressing.
 * Everything else - a network that dropped, a 5xx, a database that was not
 * there - is worth another go and must keep getting one.
 */
export function isRefused(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 400;
}

/**
 * The stable `details.rule` a refusal carries, or null when it carries none.
 *
 * The API answers a refusal it expects a client to ACT on with a discriminator
 * beside the prose - `schedule-cap`, `already-finished`,
 * `first-run-already-chosen` - and this is where that shape is read, for the
 * same reason `fieldMessages` is here: a component reaching into an error's
 * `details` itself would be a second place that knows what the envelope looks
 * like, and the first to be missed when the envelope changes.
 *
 * A caller compares the result to a literal it names, so an unknown rule reads
 * as "no rule I have words for" rather than as a crash - the same courtesy the
 * sign-in marker extends to a `signin` value this client does not know.
 */
export function refusalRule(error: unknown): string | null {
  if (!(error instanceof ApiClientError)) return null;
  const details: unknown = error.details;
  if (typeof details !== 'object' || details === null) return null;
  const rule: unknown = (details as { rule?: unknown }).rule;
  return typeof rule === 'string' ? rule : null;
}

/** The per-field sentences out of `details`, if it has the shape `parse()` sends. */
function fieldMessages(error: unknown): string[] {
  if (!(error instanceof ApiClientError)) return [];
  const details: unknown = error.details;
  if (!Array.isArray(details)) return [];

  const said: string[] = [];
  for (const issue of details as unknown[]) {
    if (typeof issue !== 'object' || issue === null) continue;
    const message: unknown = (issue as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') said.push(message);
  }
  return said;
}
